import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ProductGroupStep from '../ProductGroupStep/ProductGroupStep';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './Counters.module.css';

// Reuse the tier-label formatter idea from Floor: raw grouping label -> nice label.
// `baseLabel` controls what the entry tier is called — "Standard" (default) or
// e.g. "Level 1" — for materials the client wants numbered from 1.
function makeTierFormatter(kind, { baseLabel = 'Standard' } = {}) {
    return (rawTier) => {
        const raw = String(rawTier || '').trim();
        if (!raw || raw === '__all__') return '';
        const lc = raw.toLowerCase();
        if (lc === 'standard') return `${kind} ${baseLabel}`;
        if (lc === 'premium') {
            const n = baseLabel === 'Level 1' ? 2 : null;
            return n ? `${kind} Level ${n}` : `${kind} Premium`;
        }
        const levelMatch = raw.match(/(?:^|\s)L(?:evel)?\s*(\d+)/i);
        if (levelMatch) {
            const n = parseInt(levelMatch[1], 10);
            return n <= 1 ? `${kind} ${baseLabel}` : `${kind} Level ${n}`;
        }
        if (/level/i.test(raw)) return `${kind} ${raw}`;
        return `${kind} ${raw}`;
    };
}

const APPLY_SURFACES = ['Counter', 'Island Counter', 'Counters'];

// Filters used per material tab. `name` is matched loosely (case-insensitive
// substring) against product.name. Adjust the token list if the source data
// uses different labels — the sheet fixture uses names like "Granite Level 1".
const BASE_TABS = [
    { key: 'granite',   label: 'Granite',   filter: 'Granite'   },
    { key: 'quartz',    label: 'Quartz',    filter: 'Quartz',    baseLabel: 'Level 1' }
];

const EXTRA_TABS_PR_AF = [
    { key: 'quartzite', label: 'Quartzite', filter: 'Quartzite' },
    { key: 'marble',    label: 'Marble',    filter: 'Marble'    }
];

export default function Counters({
    stepIndex,
    room = '',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const activeFloorplanId = useSelector(state => state.visualizer.activeFloorplanId);
    const floorplans = useSelector(state => state.floorplan.floorplans);

    const communityCode = useMemo(() => {
        const fp = (floorplans || []).find(f => String(f.Id || f.id) === String(activeFloorplanId));
        return String(fp?.Code || fp?.code || '').trim().toUpperCase();
    }, [floorplans, activeFloorplanId]);

    const tabs = useMemo(() => {
        if (communityCode === 'PR' || communityCode === 'AF') {
            return [...BASE_TABS, ...EXTRA_TABS_PR_AF];
        }
        return BASE_TABS;
    }, [communityCode]);

    const [activeTab, setActiveTab] = useState(tabs[0].key);

    // If tab list shrinks (community change) and current tab is gone, reset.
    if (!tabs.some(t => t.key === activeTab)) {
        setActiveTab(tabs[0].key);
    }

    const currentTab = tabs.find(t => t.key === activeTab) || tabs[0];

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Countertop"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.tabs}>
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className={styles.tabContent}>
                <div className={styles.productArea}>
                    <ProductGroupStep
                        title=""
                        stepName="Counters"
                        seriesId="9780"
                        surfaceName="Counters"
                        applySurfaces={APPLY_SURFACES}
                        stepIndex={stepIndex}
                        room={room}
                        onViewAllApplied={onViewAllApplied}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        hideNav
                        nameFilter={currentTab.filter}
                        tierLabelFormatter={makeTierFormatter(currentTab.label, { baseLabel: currentTab.baseLabel })}
                    />
                </div>
            </div>
        </div>
    );
}

Counters.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
